import { useEventListener } from "@/src/hooks/useEventListener"
import { useCallback, useEffect, useState } from "react"

/**
 * Events triggered by CookieBot.
 */
const COOKIEBOT_EVENTS = {
    /**
     * The event is triggered when the user's consent state is ready, either from being
     * submitted or loaded from an existing cookie. Listen for this event if you need to
     * retrieve the user's consent and run additional scripts as soon as possible based on
     * consent values.
     */
    ConsentReady: "CookiebotOnConsentReady",

    /**
     * The event is triggered at the same time as the window.onload event when the user's
     * consent has been loaded - either when the user submits consent or when the user
     * navigates to a page where consent has already been submitted.
     */
    Load: "CookiebotOnLoad",

    /**
     * The event is triggered if the user accepts the use of cookies. The event is also
     * triggered if the user has consented at an earlier visit to the website.
     */
    Accept: "CookiebotOnAccept",

    /**
     * The event is triggered if the user declines the use of cookies by clicking the
     * decline-button in the cookie consent dialog. The event is also triggered if the
     * user already has declined at an earlier visit to the website.
     */
    Decline: "CookiebotOnDecline",

    /**
     * Fires when the cookie consent banner is initialized, before compiling the content
     * of the banner.
     */
    DialogInit: "CookiebotOnDialogInit",

    /**
     * Fires when the cookie consent banner is displayed to the end user.
     */
    DialogDisplay: "CookiebotOnDialogDisplay",

    /**
     * Fires when tags marked up for prior consent (e.g. the attribute
     * "data-cookieconsent") have been triggered.
     */
    TagsExecuted: "CookiebotOnTagsExecuted"
} as const satisfies Record<string, string>

/**
 * Options for the `useCookieBot` hook.
 */
type Options = {
    /**
     * Handler executed when the CookieBot "accept" event is triggered.
     */
    onAccept?: () => unknown

    /**
     * Handler executed when the CookieBot "decline" event is triggered.
     */
    onDecline?: () => unknown

    /**
     * Handler executed when the CookieBot "display" event is triggered.
     */
    onDisplay?: () => unknown
}

type Consent = {
    /**
     * True if current user has accepted necessary cookies.
     *
     * @default true
     */
    readonly necessary: boolean

    /**
     * True if current user has accepted preference cookies.
     */
    readonly preferences: boolean

    /**
     * True if current user has accepted statistics cookies.
     */
    readonly statistics: boolean

    /**
     * True if current user has accepted marketing cookies.
     */
    readonly marketing: boolean
}

const DEFAULT_CONSENT: Consent = {
    necessary: true,
    preferences: false,
    statistics: false,
    marketing: false
}

/**
 * Cookiebot can be present on `window` while `consent` is still undefined (script
 * loading, ad-block stub, or a client navigation before ConsentReady). Destructuring
 * `consent` in that state throws during render and takes down any consumer — on PDPs
 * that is ProductForm, which emptied `<main>` and left only the footer.
 */
export const readCookieBotConsent = (
    cookieBot: Pick<CookieBot, "consent"> | null | undefined
): Consent => {
    const consent = cookieBot?.consent
    if (!consent) {
        return DEFAULT_CONSENT
    }

    return {
        necessary: consent.necessary,
        marketing: consent.marketing,
        preferences: consent.preferences,
        statistics: consent.statistics
    }
}

/**
 * Read the persistent `window.Cookiebot` object, if available.
 *
 * Cookiebot's events only fire when its script executes, i.e. on a full page load. A
 * client-side navigation remounts consumers of this hook without re-firing any event,
 * so state has to be synced from the object itself. That sync must happen in an effect,
 * not in the initial state: the script loads `beforeInteractive`, so `window.Cookiebot`
 * usually exists during the hydration render, and reading it there made the client
 * render markup the server never did (the footer's cookie-preferences button).
 */
const getCookieBot = () => (typeof window === "undefined" ? undefined : window.Cookiebot)

/**
 * A React hook that allows to easily interface with CookieBot.
 */
export const useCookieBot = (options?: Options) => {
    const [loaded, setLoaded] = useState(false)
    const [consentReady, setConsentReady] = useState(false)
    const [consent, setConsent] = useState<Consent>(DEFAULT_CONSENT)

    useEffect(() => {
        const cookieBot = getCookieBot()
        if (!cookieBot) {
            return
        }

        setLoaded(true)
        setConsentReady(!!cookieBot.hasResponse)
    }, [])

    /**
     * Execute the given callback if the CookieBot is loaded.
     */
    const ifLoaded = useCallback(
        (callback: (cookieBot: CookieBot) => unknown) => {
            if (!loaded || !window.Cookiebot) {
                return
            }

            callback(window.Cookiebot)
        },
        [loaded]
    )

    /**
     * Read the consent status from CookieBot and store it the hook's state.
     */
    const loadConsent = useCallback(
        () => ifLoaded(cookieBot => setConsent(readCookieBotConsent(cookieBot))),
        [ifLoaded]
    )

    useEffect(() => {
        loadConsent()
    }, [loadConsent])

    useEventListener(COOKIEBOT_EVENTS.Accept, () => {
        loadConsent()
        options?.onAccept?.()
    })
    useEventListener(COOKIEBOT_EVENTS.Decline, () => {
        loadConsent()
        options?.onDecline?.()
    })
    useEventListener(COOKIEBOT_EVENTS.ConsentReady, () => {
        loadConsent()
        setConsentReady(true)
    })
    useEventListener(COOKIEBOT_EVENTS.DialogDisplay, options?.onDisplay)
    useEventListener(COOKIEBOT_EVENTS.Load, () => setLoaded(true))

    /**
     * Forces the cookie consent dialog to show.
     */
    const show = useCallback(() => ifLoaded(cookieBot => cookieBot.show()), [ifLoaded])

    /**
     * Shows the cookie consent dialog to the website user to renew or change the user's
     * consent state.
     */
    const renew = useCallback(() => ifLoaded(cookieBot => cookieBot.renew()), [ifLoaded])

    return {
        /**
         * Current consent status.
         */
        consent,
        /**
         * Whether CookieBot has been loaded.
         */
        loaded,
        /**
         * Whether the user's consent state has been resolved — either submitted in the
         * current session or loaded from an existing cookie (Cookiebot `OnConsentReady`).
         *
         * Use this (not `loaded`) when a consumer must run only after the cookie consent
         * decision is known and its consent events have been pushed to the dataLayer.
         */
        consentReady,
        renew,
        show
    }
}
